import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../App';

const TABLE = 'demand_calendar_days';
const EMPTY_FORM = { id: null, name: '', date: '', day_type: 'public', intensity: '1.00' };
const RECURRING_TABLE = 'recurring_events';
const EMPTY_RECURRING_FORM = { id: null, title: '', valid_from: '', valid_to: '', description: '', visibility: true, recurrence_period: 'weekly', weekdays: [4], start_time: '13:00', end_time: '14:00', event_uid: null, supersedes_id: null };
const WEEKDAY_OPTIONS = [['Monday', 0], ['Tuesday', 1], ['Wednesday', 2], ['Thursday', 3], ['Friday', 4], ['Saturday', 5], ['Sunday', 6]];
const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const formatPercent = (value) => `${(Number(value || 0) * 100).toFixed(0)}%`;
const monthLabel = (date) => date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
const dateKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const dayBefore = (dateString) => {
  const date = new Date(`${dateString}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
};
const formatRecurringDays = (event) => event.recurrence_period === 'daily'
  ? 'Every day'
  : (event.weekdays || []).map((day) => WEEKDAY_SHORT[day]).join(', ') || '-';
const formatRecurringTime = (event) => event.start_time && event.end_time
  ? `${String(event.start_time).slice(0, 5)}-${String(event.end_time).slice(0, 5)}`
  : '-';

function buildCalendarDays(month) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = new Date(first);
  const mondayOffset = (first.getDay() + 6) % 7;
  start.setDate(first.getDate() - mondayOffset);
  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
}

function getLastWorkingDayOfMonth(year, month, bankHolidays) {
  const date = new Date(Date.UTC(year, month, 0));
  while (
    date.getUTCDay() === 0
    || date.getUTCDay() === 6
    || bankHolidays.has(date.toISOString().slice(0, 10))
  ) {
    date.setUTCDate(date.getUTCDate() - 1);
  }
  return date.toISOString().slice(0, 10);
}

export default function DemandCalendar() {
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [entries, setEntries] = useState([]);
  const [recurringEvents, setRecurringEvents] = useState([]);
  const [strikes, setStrikes] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [recurringForm, setRecurringForm] = useState(EMPTY_RECURRING_FORM);
  const [recurringFormOpen, setRecurringFormOpen] = useState(false);
  const [view, setView] = useState('calendar');
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');

  const loadEntries = async () => {
    if (!supabase) {
      setStatus('Supabase is not configured');
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase.from(TABLE).select('*').order('date', { ascending: true });
    if (error) setStatus(`Load failed: ${error.message}`);
    else setEntries(data || []);
    setLoading(false);
  };

  const loadRecurringEvents = async () => {
    if (!supabase) return;
    const { data, error } = await supabase.from(RECURRING_TABLE).select('*').order('valid_from', { ascending: true });
    if (error) setStatus(`Recurring events load failed: ${error.message}`);
    else setRecurringEvents(data || []);
  };

  const loadStrikes = async () => {
    if (!supabase) return;
    const { data, error } = await supabase.from('strikes').select('name,valid_from,valid_until,severity').order('valid_from', { ascending: true });
    if (!error) setStrikes(data || []);
  };

  useEffect(() => { loadEntries(); loadRecurringEvents(); loadStrikes(); }, []);

  const entriesByDate = useMemo(() => new Map(entries.map((entry) => [entry.date, entry])), [entries]);
  const visibleEntries = view === 'calendar' ? entries : entries.filter((entry) => entry.day_type === view);
  const calendarDays = useMemo(() => buildCalendarDays(month), [month]);
  const publicHolidayDates = useMemo(() => new Set(entries
    .filter((entry) => entry.day_type === 'public')
    .map((entry) => entry.date)), [entries]);
  const paydayDates = useMemo(() => {
    const dates = new Set();
    const months = new Set(calendarDays.map((day) => `${day.getFullYear()}-${day.getMonth() + 1}`));
    months.forEach((monthKey) => {
      const [year, monthNumber] = monthKey.split('-').map(Number);
      dates.add(getLastWorkingDayOfMonth(year, monthNumber, publicHolidayDates));
    });
    return dates;
  }, [calendarDays, publicHolidayDates]);

  const recurringByDate = useMemo(() => {
    const map = new Map();
    for (const event of recurringEvents) {
      if (!event.visibility) continue;
      const weekdays = event.recurrence_period === 'daily' ? [0, 1, 2, 3, 4, 5, 6] : (event.weekdays || []);
      const start = new Date(`${event.valid_from}T00:00:00Z`);
      const end = new Date(`${event.valid_to}T00:00:00Z`);
      for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
        const dow = (d.getUTCDay() + 6) % 7;
        if (!weekdays.includes(dow)) continue;
        const key = d.toISOString().slice(0, 10);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(event);
      }
    }
    return map;
  }, [recurringEvents]);

  const strikesByDate = useMemo(() => {
    const map = new Map();
    for (const strike of strikes) {
      const start = new Date(strike.valid_from);
      const end = new Date(strike.valid_until);
      for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
        const key = d.toISOString().slice(0, 10);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(strike);
      }
    }
    return map;
  }, [strikes]);
  const setField = (name, value) => setForm((current) => ({ ...current, [name]: value }));
  const setRecurringField = (name, value) => setRecurringForm((current) => ({ ...current, [name]: value }));

  const openNew = (date = '') => {
    setStatus('');
    setForm({ ...EMPTY_FORM, date });
  };

  const editEntry = (entry) => {
    setStatus('');
    setForm({ ...entry, intensity: Number(entry.intensity).toFixed(2) });
  };

  const save = async (event) => {
    event.preventDefault();
    const intensity = Number(form.intensity);
    if (!form.name.trim() || !form.date || !Number.isFinite(intensity) || intensity < 0 || intensity > 1) {
      setStatus('Enter a name, date, and intensity between 0.00 and 1.00.');
      return;
    }
    const row = { name: form.name.trim(), date: form.date, day_type: form.day_type, intensity };
    const result = form.id
      ? await supabase.from(TABLE).update(row).eq('id', form.id).select().single()
      : await supabase.from(TABLE).insert(row).select().single();
    if (result.error) {
      setStatus(`Save failed: ${result.error.message}`);
      return;
    }
    setEntries((current) => form.id
      ? current.map((entry) => entry.id === form.id ? result.data : entry)
      : [...current, result.data].sort((a, b) => a.date.localeCompare(b.date)));
    setForm(EMPTY_FORM);
    setStatus('Saved');
  };

  const remove = async () => {
    if (!form.id || !window.confirm(`Delete ${form.name}?`)) return;
    const { error } = await supabase.from(TABLE).delete().eq('id', form.id);
    if (error) {
      setStatus(`Delete failed: ${error.message}`);
      return;
    }
    setEntries((current) => current.filter((entry) => entry.id !== form.id));
    setForm(EMPTY_FORM);
    setStatus('Deleted');
  };

  const openNewRecurring = () => {
    setStatus('');
    setRecurringForm(EMPTY_RECURRING_FORM);
    setRecurringFormOpen(true);
  };

  const editRecurring = (event) => {
    setStatus('');
    setRecurringForm({
      ...EMPTY_RECURRING_FORM,
      ...event,
      visibility: Boolean(event.visibility),
      recurrence_period: event.recurrence_period || 'weekly',
      weekdays: event.weekdays || [],
      start_time: event.start_time ? String(event.start_time).slice(0, 5) : '',
      end_time: event.end_time ? String(event.end_time).slice(0, 5) : ''
    });
    setRecurringFormOpen(true);
  };

  const toggleRecurringWeekday = (day) => setRecurringForm((current) => ({
    ...current,
    weekdays: current.weekdays.includes(day)
      ? current.weekdays.filter((value) => value !== day)
      : [...current.weekdays, day].sort((a, b) => a - b)
  }));

  const saveRecurring = async (event) => {
    event.preventDefault();
    const title = recurringForm.title.trim();
    const weekdays = recurringForm.recurrence_period === 'daily' ? [0, 1, 2, 3, 4, 5, 6] : recurringForm.weekdays;
    if (!title || !recurringForm.valid_from || !recurringForm.valid_to || !recurringForm.start_time || !recurringForm.end_time) {
      setStatus('Enter a title, validity dates, and start/end times for the recurring event.');
      return;
    }
    if (recurringForm.valid_to < recurringForm.valid_from) {
      setStatus('Valid to must be on or after valid from.');
      return;
    }
    if (recurringForm.end_time <= recurringForm.start_time) {
      setStatus('End time must be after start time.');
      return;
    }
    if (weekdays.length === 0) {
      setStatus('Select at least one day for a weekly recurring event.');
      return;
    }
    const row = {
      title,
      valid_from: recurringForm.valid_from,
      valid_to: recurringForm.valid_to,
      description: recurringForm.description.trim(),
      visibility: Boolean(recurringForm.visibility),
      recurrence_period: recurringForm.recurrence_period,
      weekdays,
      start_time: recurringForm.start_time,
      end_time: recurringForm.end_time,
      event_uid: recurringForm.event_uid || `rec_${crypto.randomUUID()}`,
      supersedes_id: recurringForm.id || null
    };
    if (recurringForm.id) {
      // Past entries keep their original schedule: the previous version ends just before the new one starts.
      const previous = recurringEvents.find((item) => item.id === recurringForm.id);
      if (previous && recurringForm.valid_from > previous.valid_from) {
        const closeResult = await supabase.from(RECURRING_TABLE)
          .update({ valid_to: dayBefore(recurringForm.valid_from), updated_at: new Date().toISOString() })
          .eq('id', recurringForm.id);
        if (closeResult.error) {
          setStatus(`Recurring event save failed: ${closeResult.error.message}`);
          return;
        }
      } else if (previous) {
        const deleteResult = await supabase.from(RECURRING_TABLE).delete().eq('id', recurringForm.id);
        if (deleteResult.error) {
          setStatus(`Recurring event save failed: ${deleteResult.error.message}`);
          return;
        }
      }
    }
    const { error } = await supabase.from(RECURRING_TABLE).insert(row);
    if (error) {
      setStatus(`Recurring event save failed: ${error.message}`);
      return;
    }
    await loadRecurringEvents();
    setRecurringForm(EMPTY_RECURRING_FORM);
    setRecurringFormOpen(false);
    setStatus(recurringForm.id ? 'Future recurring entries updated' : 'Recurring event saved');
  };

  const removeRecurring = async () => {
    if (!recurringForm.id || !window.confirm(`Delete ${recurringForm.title}?`)) return;
    const { error } = await supabase.from(RECURRING_TABLE).delete().eq('id', recurringForm.id);
    if (error) {
      setStatus(`Recurring event delete failed: ${error.message}`);
      return;
    }
    setRecurringEvents((current) => current.filter((item) => item.id !== recurringForm.id));
    setRecurringForm(EMPTY_RECURRING_FORM);
    setRecurringFormOpen(false);
    setStatus('Recurring event deleted');
  };

  return <section className="holiday-calendar">
    <div className="calendar-header">
      <div><span className="eyebrow">DEMAND MODEL INPUT</span><h2>{view === 'calendar' ? 'Calendar' : view === 'public' ? 'Public holidays' : view === 'special' ? 'Special days' : 'Recurring events'}</h2></div>
      <div className="calendar-actions"><span className="dashboard-toast">{loading ? 'Loading...' : status}</span>{view !== 'recurring' && <><button className="button muted" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}>Previous</button><strong>{monthLabel(month)}</strong><button className="button muted" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}>Next</button></>}{view === 'calendar' && <button className="button primary" onClick={() => openNew(dateKey(month))}>Add day</button>}{view === 'recurring' && <button className="button primary" onClick={openNewRecurring}>Add recurring event</button>}</div>
    </div>
    <nav className="calendar-subtabs" aria-label="Calendar views"><button className={view === 'calendar' ? 'active' : ''} onClick={() => setView('calendar')}>Calendar</button><button className={view === 'public' ? 'active' : ''} onClick={() => setView('public')}>Public holidays</button><button className={view === 'special' ? 'active' : ''} onClick={() => setView('special')}>Special days</button><button className={view === 'recurring' ? 'active' : ''} onClick={() => setView('recurring')}>Recurring events</button></nav>
    {view === 'calendar' && <><div className="calendar-grid calendar-weekdays">{['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => <div key={day}>{day}</div>)}</div>
    <div className="calendar-grid calendar-days">{calendarDays.map((day) => {
      const key = dateKey(day);
      const entry = entriesByDate.get(key);
      const inMonth = day.getMonth() === month.getMonth();
      return <div className={`calendar-day${inMonth ? '' : ' outside-month'}`} key={key} onDoubleClick={() => openNew(key)}>
        <div className="calendar-day-number">{day.getDate()}</div>
        {paydayDates.has(key) && <div className="payday-entry">Payday</div>}
        {entry && <button className={`holiday-entry ${entry.day_type}`} onClick={() => editEntry(entry)}><strong>{entry.name}</strong><small>{formatPercent(entry.intensity)}</small></button>}
        {(strikesByDate.get(key) || []).map((strike) => <div className="calendar-tag strike-tag" key={strike.name} title={`Strike: ${strike.name}`}>{strike.name}</div>)}
        {(recurringByDate.get(key) || []).map((event) => <div className="calendar-tag recurring-tag" key={event.id} title={`${event.title} ${event.start_time}-${event.end_time}`}>{event.title}</div>)}
      </div>;
    })}</div></>}
    {form.date && <div className="holiday-editor">
      <div className="editor-heading"><div><span className="eyebrow">CALENDAR ENTRY</span><h3>{form.id ? 'Edit day' : 'Add day'}</h3></div>{form.id && <button className="delete" onClick={remove}>Delete</button>}</div>
      <form onSubmit={save} className="holiday-form">
        <label>Name<input value={form.name} onChange={(event) => setField('name', event.target.value)} placeholder="Christmas Day" /></label>
        <label>Date<input type="date" value={form.date} onChange={(event) => setField('date', event.target.value)} /></label>
        <label>Type<select value={form.day_type} onChange={(event) => setField('day_type', event.target.value)}><option value="public">Public holiday</option><option value="special">Special day</option></select></label>
        <label>Intensity<input type="number" min="0" max="1" step="0.01" value={form.intensity} onChange={(event) => setField('intensity', event.target.value)} /><small>Decimal: 1.00 = 100%</small></label>
        <div className="modal-actions"><button type="button" className="button muted" onClick={() => setForm(EMPTY_FORM)}>Cancel</button><button type="submit" className="button primary">Save</button></div>
      </form>
    </div>}
    {recurringFormOpen && <div className="holiday-editor recurring-editor">
      <div className="editor-heading"><div><span className="eyebrow">RECURRING EVENT</span><h3>{recurringForm.id ? 'Edit recurring event' : 'Add recurring event'}</h3></div>{recurringForm.id && <button className="delete" onClick={removeRecurring}>Delete</button>}</div>
      <form onSubmit={saveRecurring} className="holiday-form recurring-form">
        <label>Title<input value={recurringForm.title} onChange={(event) => setRecurringField('title', event.target.value)} placeholder="Friday prayers" /></label>
        <label>Period<select value={recurringForm.recurrence_period} onChange={(event) => setRecurringField('recurrence_period', event.target.value)}><option value="daily">Daily</option><option value="weekly">Weekly</option></select></label>
        <label>Start time<input type="time" value={recurringForm.start_time} onChange={(event) => setRecurringField('start_time', event.target.value)} /></label>
        <label>End time<input type="time" value={recurringForm.end_time} onChange={(event) => setRecurringField('end_time', event.target.value)} /></label>
        <label>Valid from<input type="date" value={recurringForm.valid_from} onChange={(event) => setRecurringField('valid_from', event.target.value)} /></label>
        <label>Valid to<input type="date" value={recurringForm.valid_to} onChange={(event) => setRecurringField('valid_to', event.target.value)} /></label>
        {recurringForm.recurrence_period === 'weekly' && <fieldset className="weekday-picker wide-field"><legend>Repeats on</legend>{WEEKDAY_OPTIONS.map(([label, value]) => <label key={value}><input type="checkbox" checked={recurringForm.weekdays.includes(value)} onChange={() => toggleRecurringWeekday(value)} /> {label}</label>)}</fieldset>}
        {recurringForm.id && <p className="recurring-note wide-field">Saving edits future entries only. Past occurrences keep their current days and times.</p>}
        <label className="wide-field">Description<textarea value={recurringForm.description} onChange={(event) => setRecurringField('description', event.target.value)} placeholder="Optional demand context" rows="2" /></label>
        <label className="visibility-field"><input type="checkbox" checked={recurringForm.visibility} onChange={(event) => setRecurringField('visibility', event.target.checked)} /> Visible on calendar</label>
        <div className="modal-actions"><button type="button" className="button muted" onClick={() => { setRecurringForm(EMPTY_RECURRING_FORM); setRecurringFormOpen(false); }}>Cancel</button><button type="submit" className="button primary">{recurringForm.id ? 'Save future entries' : 'Save'}</button></div>
      </form>
    </div>}
    {view === 'recurring' && <div className="holiday-list-panel recurring-list-panel">
      <div className="live-header"><div><span className="eyebrow">SCHEDULED INPUTS</span><h2>Recurring events</h2></div><span className="draft-status">{recurringEvents.length} entries</span></div>
      <div className="table-scroll"><table className="holiday-list recurring-list"><thead><tr><th>Title</th><th>Period</th><th>Days</th><th>Time</th><th>Valid from</th><th>Valid to</th><th>Description</th><th>Visibility</th><th>Action</th></tr></thead><tbody>{recurringEvents.map((event) => <tr key={event.id}><td><strong>{event.title}</strong><small className="mono">{event.event_uid || '-'}</small></td><td>{event.recurrence_period === 'daily' ? 'Daily' : 'Weekly'}</td><td>{formatRecurringDays(event)}</td><td className="mono">{formatRecurringTime(event)}</td><td className="mono">{event.valid_from}</td><td className="mono">{event.valid_to}</td><td>{event.description || '-'}</td><td>{event.visibility ? 'Visible' : 'Hidden'}</td><td><button className="button muted" onClick={() => editRecurring(event)}>Edit</button></td></tr>)}</tbody></table></div>
    </div>}
    {view !== 'calendar' && view !== 'recurring' && <div className="holiday-list-panel">
      <div className="live-header"><div><span className="eyebrow">EDITABLE LIST</span><h2>{view === 'public' ? 'All public holidays' : 'All special days'}</h2></div><span className="draft-status">{visibleEntries.length} entries</span></div>
      <div className="table-scroll"><table className="holiday-list"><thead><tr><th>Name</th><th>Date</th><th>Type</th><th>Intensity</th><th>Action</th></tr></thead><tbody>{visibleEntries.map((entry) => <tr key={entry.id}><td><strong>{entry.name}</strong></td><td className="mono">{entry.date}</td><td>{entry.day_type === 'public' ? 'Public holiday' : 'Special day'}</td><td>{formatPercent(entry.intensity)}</td><td><button className="button muted" onClick={() => editEntry(entry)}>Edit</button></td></tr>)}</tbody></table></div>
    </div>}
  </section>;
}
